import "./styles.css";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";

import { playLevelMusic, playMenuMusic, updateMusicSettings } from "./audio-manager";
import { GameView } from "./GameView";
import { Hud } from "./Hud";
import { LandingPage } from "./LandingPage";
import { Leaderboard } from "./Leaderboard";
import { LevelSelect } from "./LevelSelect";
import { Lobby } from "./Lobby";
import { ModeSelect } from "./ModeSelect";
import { PauseOverlay } from "./PauseOverlay";
import { ResultScreen } from "./ResultScreen";
import { SettingsPage } from "./SettingsPage";
import { SoloSim } from "./solo-sim";
import { LEVELS } from "../level";

import type { ResultInfo } from "./ResultScreen";
import type { LobbyPlayer, PlayerTickState, ServerMessage } from "../shared";

type TickLike = {
	players: PlayerTickState[];
	finishOrder: string[];
	finishTimesMs: Record<string, number>;
};

const NAME_STORAGE_KEY = "rk-beach-rampage:name";
const SOUND_ON_STORAGE_KEY = "rk-beach-rampage:sound-on";
const VOLUME_STORAGE_KEY = "rk-beach-rampage:volume";
const CRT_STORAGE_KEY = "rk-beach-rampage:crt-effect";

type Screen =
	| "landing"
	| "mode-select"
	| "level-select"
	| "settings"
	| "lobby"
	| "playing"
	| "paused"
	| "result"
	| "leaderboard";
type Mode = "solo" | "multi";
type PendingLevelAction = "solo" | "create";

function readRoomCodeFromPath(): string | null {
	const match = /^\/room\/([0-9A-Za-z]{6})$/.exec(location.pathname);
	return match ? match[1].toUpperCase() : null;
}

function App() {
	const [name, setName] = useState(() => localStorage.getItem(NAME_STORAGE_KEY) ?? "");
	const [playerId, setPlayerId] = useState<string | null>(null);
	const [screen, setScreen] = useState<Screen>(() => (readRoomCodeFromPath() ? "mode-select" : "landing"));
	const [preLeaderboardScreen, setPreLeaderboardScreen] = useState<Screen>("mode-select");
	const [preSettingsScreen, setPreSettingsScreen] = useState<Screen>("mode-select");
	const [soundOn, setSoundOn] = useState(() => localStorage.getItem(SOUND_ON_STORAGE_KEY) !== "false");
	const [volume, setVolume] = useState(() => Number(localStorage.getItem(VOLUME_STORAGE_KEY) ?? "70"));
	const [crtEffect, setCrtEffect] = useState(() => localStorage.getItem(CRT_STORAGE_KEY) !== "false");
	const [mode, setMode] = useState<Mode | null>(null);
	const [pendingLevelAction, setPendingLevelAction] = useState<PendingLevelAction | null>(null);
	const [levelIndex, setLevelIndex] = useState(0);
	const [roomCode, setRoomCode] = useState<string | null>(null);
	const [lobbyPlayers, setLobbyPlayers] = useState<LobbyPlayer[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [socket, setSocket] = useState<WebSocket | null>(null);
	const [localPlayerState, setLocalPlayerState] = useState<PlayerTickState | undefined>(undefined);
	const [opponentState, setOpponentState] = useState<PlayerTickState | undefined>(undefined);
	const [finishOrder, setFinishOrder] = useState<string[]>([]);
	const [finishTimesMs, setFinishTimesMs] = useState<Record<string, number>>({});
	const [raceStartAt, setRaceStartAt] = useState<number | null>(null);
	const [now, setNow] = useState(() => Date.now());
	const soloSimRef = useRef<SoloSim | null>(null);
	const soloReportedRef = useRef(false);
	const [isFullscreen, setIsFullscreen] = useState(() => !!document.fullscreenElement);

	// Shared by both the multiplayer WebSocket listener and solo's local
	// simulation callback so the two paths update the same React state the
	// same way — the DO tick message and SoloSim's snapshot are shaped alike.
	const handleTick = useCallback(
		(message: TickLike) => {
			setLocalPlayerState(message.players.find((p) => p.playerId === playerId));
			setOpponentState(message.players.find((p) => p.playerId !== playerId));
			setFinishOrder(message.finishOrder);
			setFinishTimesMs(message.finishTimesMs);
		},
		[playerId],
	);

	useEffect(() => {
		fetch("/api/player")
			.then((response) => response.json() as Promise<{ playerId: string }>)
			.then((body) => setPlayerId(body.playerId));
	}, []);

	useEffect(() => {
		localStorage.setItem(NAME_STORAGE_KEY, name);
	}, [name]);

	useEffect(() => {
		localStorage.setItem(SOUND_ON_STORAGE_KEY, String(soundOn));
	}, [soundOn]);

	useEffect(() => {
		localStorage.setItem(VOLUME_STORAGE_KEY, String(volume));
	}, [volume]);

	useEffect(() => {
		localStorage.setItem(CRT_STORAGE_KEY, String(crtEffect));
	}, [crtEffect]);

	// Background music: the level's own track while actually playing/paused,
	// the menu theme on every other screen. Re-selecting only on screen/level
	// changes (not on every soundOn/volume tweak) avoids restarting the track
	// mid-run — see the settings-sync effect below for that.
	useEffect(() => {
		if (screen === "playing" || screen === "paused") {
			playLevelMusic((LEVELS[levelIndex] ?? LEVELS[0]).slug, soundOn, volume);
		} else {
			playMenuMusic(soundOn, volume);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [screen, levelIndex]);

	useEffect(() => {
		updateMusicSettings(soundOn, volume);
	}, [soundOn, volume]);

	// Tracks fullscreen state driven from any source (our own toggle button, the
	// browser's native Esc-to-exit, F11, etc.) so the toggle button's icon/label
	// always reflects reality instead of assuming its own click caused the change.
	useEffect(() => {
		function onFullscreenChange() {
			setIsFullscreen(!!document.fullscreenElement);
		}
		document.addEventListener("fullscreenchange", onFullscreenChange);
		return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
	}, []);

	function toggleFullscreen() {
		if (document.fullscreenElement) {
			document.exitFullscreen().catch(() => {});
		} else {
			document.documentElement.requestFullscreen().catch(() => {});
		}
	}

	// Ticks a countdown clock while a multiplayer race-start is pending, so the
	// "playing" screen can render "3… 2… 1…" — server-side movement is already
	// gated on raceStartAt independently (game-session.ts), this is display only.
	useEffect(() => {
		if (raceStartAt === null || Date.now() >= raceStartAt) return;
		const interval = setInterval(() => setNow(Date.now()), 200);
		return () => clearInterval(interval);
	}, [raceStartAt]);

	// The local player's own run ending (finish or game-over) is what moves the
	// screen to "result" — this is a client-side transition, not server-pushed,
	// since each player's run can end at a different tick.
	useEffect(() => {
		if (screen !== "playing") return;
		if (localPlayerState && (localPlayerState.finished || localPlayerState.gameOver)) {
			setScreen("result");
		}
	}, [screen, localPlayerState]);

	// Esc toggles pause, solo only — solo has no server round trip to notify
	// anymore (see startSolo), so this is purely a client-side freeze of the
	// GameView animation loop via the `paused` prop.
	useEffect(() => {
		if (mode !== "solo") return;
		function onKeyDown(event: KeyboardEvent) {
			if (event.key !== "Escape") return;
			setScreen((current) => {
				if (current === "playing") return "paused";
				if (current === "paused") return "playing";
				return current;
			});
		}
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [mode]);

	// Solo's run is entirely client-side (SoloSim), so the only server contact
	// is a single fire-once POST once the run finishes, to record the score —
	// same leaderboard write the DO used to do from inside its tick loop.
	useEffect(() => {
		if (mode !== "solo" || !playerId) return;
		if (!localPlayerState?.finished || soloReportedRef.current) return;
		soloReportedRef.current = true;
		const finishTimeMs = finishTimesMs[playerId] ?? 0;
		fetch("/api/solo/finish", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ score: localPlayerState.score, finishTimeMs, name, levelIndex }),
		}).catch(() => {});
	}, [mode, playerId, localPlayerState, finishTimesMs, name, levelIndex]);

	function connectToRoom(code: string, selectedMode: "multi", createLevelIndex?: number) {
		if (!playerId || !name) return;
		setMode(selectedMode);
		const levelParam = createLevelIndex !== undefined ? `&level=${createLevelIndex}` : "";
		const ws = new WebSocket(
			`${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/session/connect?room=${code}&playerId=${playerId}&name=${encodeURIComponent(name)}&mode=${selectedMode}${levelParam}`,
		);
		ws.addEventListener("message", (event) => {
			const message = JSON.parse(event.data as string) as ServerMessage;
			if (message.type === "hello") {
				setRoomCode(message.room.code);
				setLevelIndex(message.levelIndex);
				history.replaceState(null, "", `/room/${message.room.code}`);
				setScreen("lobby");
			} else if (message.type === "lobby") {
				setLobbyPlayers(message.players);
			} else if (message.type === "race-start") {
				setRaceStartAt(message.startAtMs);
				setScreen("playing");
			} else if (message.type === "room-full") {
				setError("This room already has 2 players.");
				ws.close();
			} else if (message.type === "tick") {
				handleTick(message);
			}
		});
		setSocket(ws);
	}

	function startSolo(selectedLevelIndex: number) {
		if (!playerId) return;
		setMode("solo");
		setLevelIndex(selectedLevelIndex);
		soloReportedRef.current = false;
		soloSimRef.current = new SoloSim(playerId, selectedLevelIndex);
		setScreen("playing");
	}

	async function createRoom(selectedLevelIndex: number) {
		const response = await fetch("/room/new");
		const body = (await response.json()) as { code: string };
		connectToRoom(body.code, "multi", selectedLevelIndex);
	}

	function joinRoom(code: string) {
		if (!code) return;
		connectToRoom(code.toUpperCase(), "multi");
	}

	function openLevelSelect(action: PendingLevelAction) {
		setPendingLevelAction(action);
		setScreen("level-select");
	}

	function chooseLevel(selectedLevelIndex: number) {
		if (pendingLevelAction === "solo") startSolo(selectedLevelIndex);
		else if (pendingLevelAction === "create") void createRoom(selectedLevelIndex);
		setPendingLevelAction(null);
	}

	function sendReady() {
		socket?.send(JSON.stringify({ type: "ready" }));
	}

	function resetToModeSelect() {
		socket?.close();
		setSocket(null);
		soloSimRef.current = null;
		soloReportedRef.current = false;
		setRoomCode(null);
		setMode(null);
		setPendingLevelAction(null);
		setLevelIndex(0);
		setLobbyPlayers([]);
		setLocalPlayerState(undefined);
		setOpponentState(undefined);
		setFinishOrder([]);
		setFinishTimesMs({});
		setRaceStartAt(null);
		setError(null);
		setScreen("mode-select");
		history.replaceState(null, "", "/");
	}

	function openLeaderboard() {
		setPreLeaderboardScreen(screen);
		setScreen("leaderboard");
	}

	function openSettings() {
		setPreSettingsScreen(screen);
		setScreen("settings");
	}

	function computeResult(): ResultInfo {
		const dnf = !!localPlayerState?.gameOver && !localPlayerState.finished;
		const place = playerId && finishOrder.includes(playerId) ? finishOrder.indexOf(playerId) + 1 : null;
		const timeMs = playerId && finishTimesMs[playerId] !== undefined ? finishTimesMs[playerId] : null;

		let opponent: ResultInfo["opponent"] = null;
		if (mode === "multi") {
			if (!opponentState || (!opponentState.finished && !opponentState.gameOver)) {
				opponent = { status: "racing" };
			} else if (finishOrder.includes(opponentState.playerId)) {
				opponent = {
					status: "finished",
					place: finishOrder.indexOf(opponentState.playerId) + 1,
					timeMs: finishTimesMs[opponentState.playerId],
				};
			} else {
				opponent = { status: "dnf" };
			}
		}

		const timeGapMs =
			playerId &&
			opponentState &&
			finishTimesMs[playerId] !== undefined &&
			finishTimesMs[opponentState.playerId] !== undefined
				? Math.abs(finishTimesMs[playerId] - finishTimesMs[opponentState.playerId])
				: null;

		return { place, dnf, timeMs, opponent, timeGapMs };
	}

	const overlay = crtEffect ? <div className="scanline-overlay" aria-hidden="true" /> : null;
	const fullscreenButton = (
		<button
			type="button"
			className="fullscreen-toggle"
			onClick={toggleFullscreen}
			title={isFullscreen ? "Exit full screen" : "Enter full screen"}
			aria-label={isFullscreen ? "Exit full screen" : "Enter full screen"}
		>
			{isFullscreen ? "⤡" : "⤢"}
		</button>
	);

	if (screen === "landing") {
		return (
			<>
				{overlay}
				{fullscreenButton}
				<LandingPage onEnter={() => setScreen("mode-select")} />
			</>
		);
	}

	if (screen === "settings") {
		return (
			<>
				{overlay}
				{fullscreenButton}
				<SettingsPage
					name={name}
					onNameChange={setName}
					soundOn={soundOn}
					onSoundOnChange={setSoundOn}
					volume={volume}
					onVolumeChange={setVolume}
					crtEffect={crtEffect}
					onCrtEffectChange={setCrtEffect}
					onBack={() => setScreen(preSettingsScreen)}
				/>
			</>
		);
	}

	if (screen === "leaderboard") {
		return (
			<>
				{overlay}
				{fullscreenButton}
				<div className="App">
					<h1>RK Beach Rampage</h1>
					<Leaderboard onClose={() => setScreen(preLeaderboardScreen)} />
				</div>
			</>
		);
	}

	if (screen === "level-select") {
		return (
			<>
				{overlay}
				{fullscreenButton}
				<LevelSelect onSelect={chooseLevel} onBack={() => setScreen("mode-select")} />
			</>
		);
	}

	if (screen === "lobby" && roomCode) {
		return (
			<>
				{overlay}
				{fullscreenButton}
				<Lobby code={roomCode} players={lobbyPlayers} localPlayerId={playerId ?? ""} onReady={sendReady} />
			</>
		);
	}

	const activeLevel = LEVELS[levelIndex] ?? LEVELS[0];
	const isSoloReady = mode === "solo" && soloSimRef.current;
	if ((screen === "playing" || screen === "paused") && (socket || isSoloReady) && playerId) {
		const countingDown = raceStartAt !== null && now < raceStartAt;
		return (
			<>
				{overlay}
				<div className="App">
					<h1>RK Beach Rampage</h1>
					<p className="level-banner">
						Level {levelIndex + 1}: {activeLevel.name} — {activeLevel.blurb}
					</p>
					<Hud player={localPlayerState} />
					<GameView
						socket={socket}
						localPlayerId={playerId}
						level={activeLevel}
						soloSim={soloSimRef.current ?? undefined}
						paused={screen === "paused"}
						onTick={mode === "solo" ? handleTick : undefined}
					/>
					<p className="controls-hint">
						Move: ←/→ or A/D — Jump: ↑, W, or Space{mode === "solo" && " — Esc to pause"}
					</p>
					{countingDown && (
						<div className="countdown-overlay">{Math.max(1, Math.ceil((raceStartAt! - now) / 1000))}</div>
					)}
					{screen === "paused" && (
						<PauseOverlay onResume={() => setScreen("playing")} onSettings={openSettings} onQuit={resetToModeSelect} />
					)}
				</div>
			</>
		);
	}

	if (screen === "result") {
		const hasNextLevel = mode === "solo" && levelIndex < LEVELS.length - 1;
		return (
			<>
				{overlay}
				{fullscreenButton}
				<ResultScreen
					result={computeResult()}
					levelName={activeLevel.name}
					onPlayAgain={resetToModeSelect}
					onViewLeaderboard={openLeaderboard}
					onNextLevel={hasNextLevel ? () => startSolo(levelIndex + 1) : undefined}
				/>
			</>
		);
	}

	return (
		<>
			{overlay}
			{fullscreenButton}
			<ModeSelect
				name={name}
				onNameChange={setName}
				initialRoomCode={readRoomCodeFromPath() ?? ""}
				onStartSolo={() => openLevelSelect("solo")}
				onCreateRoom={() => openLevelSelect("create")}
				onJoinRoom={joinRoom}
				onViewLeaderboard={openLeaderboard}
				onOpenSettings={openSettings}
				error={error}
			/>
		</>
	);
}

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
createRoot(document.getElementById("root")!).render(<App />);

if ("serviceWorker" in navigator) {
	window.addEventListener("load", () => {
		navigator.serviceWorker.register("/sw.js").catch(() => {});
	});
}
